"use client";

import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import type { SuccessState } from "./AgenticCartCheckout.types";

export function AgenticCartSuccess({
  bundle,
  success,
}: {
  bundle: AgenticCartBundleSummaryResponse;
  success: SuccessState;
}) {
  return (
    <div role="main" className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl rounded-lg border border-success/30 bg-ish-bg-card p-6 shadow-sm">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-success">
          Payment Confirmed
        </p>
        <p
          role="heading"
          aria-level={1}
          className="font-serif text-2xl font-bold text-ish-dark sm:text-3xl"
        >
          {bundle.name}
        </p>
        <p className="mt-3 text-sm leading-6 text-ish-muted">
          {bundle.sankalp_program
            ? "Your private Sankalp Passport is ready. Start when your kit and reading are in hand."
            : bundle.requires_shipping
              ? "Your report is being prepared and your remedies are on the way to your address."
              : "Your digital readings are being prepared. Open your reading page to find the reports included in your purchase."}
        </p>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-md border border-ish-border-light p-3">
            <dt className="text-xs uppercase tracking-[0.1em] text-ish-faint">Cart ID</dt>
            <dd className="mt-1 font-semibold text-ish-dark">{success.order.cart_id}</dd>
          </div>
          <div className="rounded-md border border-ish-border-light p-3">
            <dt className="text-xs uppercase tracking-[0.1em] text-ish-faint">Fulfillment</dt>
            <dd className="mt-1 font-semibold text-ish-dark">
              {success.result.shopify_mirror_status}
            </dd>
          </div>
        </dl>
        {success.result.passport_url ? (
          <a
            href={success.result.passport_url}
            className="btn-sacred mt-6 inline-flex rounded-lg px-5 py-3 text-sm font-bold"
          >
            Open Sankalp Passport
          </a>
        ) : success.result.download_url ? (
          <a
            href={success.result.download_url}
            className="btn-sacred mt-6 inline-flex rounded-lg px-5 py-3 text-sm font-bold"
          >
            Open report
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function PlanSummary({ bundle }: { bundle: AgenticCartBundleSummaryResponse | null }) {
  const physicalItems = bundle?.line_items.filter((item) => item.kind === "physical_product") ?? [];
  return (
    <div className="rounded-lg border border-ish-border bg-ish-bg-card p-5 shadow-sm sm:p-6">
      <p role="heading" aria-level={2} className="font-serif text-lg font-bold text-ish-dark">
        Plan summary
      </p>
      <div className="mt-4 flex items-end justify-between border-b border-ish-border-light pb-4">
        <span className="text-sm text-ish-muted">Total</span>
        <span className="text-3xl font-bold text-ish-dark">
          {bundle ? `Rs. ${bundle.price_inr}` : "Rs. ..."}
        </span>
      </div>

      <ul className="mt-4 space-y-3">
        {bundle?.sankalp_program ? (
          <>
            <SummaryItem label="Personalised Shani reading" meta="Recomputed from your kundali" />
            <SummaryItem label="21-day Sankalp Passport" meta="Daily steps and check-ins" />
            <SummaryItem label="Blocker support" meta="Tell us when the routine cannot continue" />
          </>
        ) : null}
        {(bundle?.line_items ?? []).map((item) => (
          <li key={`${item.kind}:${item.reference_id}`} className="flex gap-3 text-sm">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
            <span>
              <span className="block font-semibold text-ish-dark">{item.label}</span>
              <span className="text-xs text-ish-muted">{lineItemMeta(item.kind)}</span>
            </span>
          </li>
        ))}
      </ul>

      {physicalItems.length > 0 && (
        <p className="mt-5 rounded-md bg-ish-accent-light px-3 py-2 text-xs leading-5 text-ish-muted">
          Your remedies are dispatched to your delivery address after payment is confirmed.
        </p>
      )}
      {bundle?.followup_routine && (
        <p className="mt-3 rounded-md border border-ish-border-light px-3 py-2 text-xs leading-5 text-ish-muted">
          {bundle.followup_routine}
        </p>
      )}
    </div>
  );
}

function SummaryItem({ label, meta }: { label: string; meta: string }) {
  return (
    <li className="flex gap-3 text-sm">
      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
      <span>
        <span className="block font-semibold text-ish-dark">{label}</span>
        <span className="text-xs text-ish-muted">{meta}</span>
      </span>
    </li>
  );
}

function lineItemMeta(kind: string): string {
  if (kind === "digital_report") return "Personalised Vedic report";
  if (kind === "physical_product") return "Sacred remedy, shipped to you";
  if (kind === "puja_booking") return "Puja coordination";
  return "Ishvaram service";
}
